import { useId } from 'react';
import { clsx } from 'clsx';
import i18n from 'helpers/i18n';
import messages from './messages';
import { CheckboxWithLabel } from 'components/Organization/Checkbox';
import { Select } from 'components/Organization/Select';
import {
  getSubscriptionProductRoles,
  subscriptionRequiresRole
} from '../subscriptionProductRoles';

export type AccountSubscriptionOption = {
  id: number;
  name: string;
  max_seats: number;
  seats_used?: number;
  users: number;
  product: { name: string; display_name: string };
};

export type SubscriptionSeatFormValue = {
  subscription_id: number;
  role: string;
};

function getRemainingSeats(subscription: AccountSubscriptionOption) {
  if (!subscription.max_seats) {
    return null;
  }

  const seatsUsed = subscription.seats_used ?? subscription.users;
  return subscription.max_seats - seatsUsed;
}

function formatSubscriptionLabel(subscription: AccountSubscriptionOption) {
  const name = subscription.name || subscription.product.display_name;
  const remainingSeats = getRemainingSeats(subscription);

  if (
    subscription.product.name !== 'data_collection' &&
    remainingSeats !== null &&
    remainingSeats <= 0
  ) {
    return (
      <>
        <span>{name}</span>
        <span className="block text-charcoal-500 font-normal text-sm">
          {i18n.ft(messages.fields.noSeatsRemaining)}
        </span>
      </>
    );
  }

  return name;
}

interface SubscriptionSeatsFieldProps {
  subscriptions: AccountSubscriptionOption[];
  value: SubscriptionSeatFormValue[];
  onChange: (seats: SubscriptionSeatFormValue[]) => void;
  roleErrors?: Record<number, string>;
  apiError?: string;
}

export function SubscriptionSeatsField({
  subscriptions,
  value,
  onChange,
  roleErrors = {},
  apiError
}: SubscriptionSeatsFieldProps) {
  const groupId = useId();
  const selectedIds = value.map(seat => seat.subscription_id);

  function toggleSubscription(subscriptionId: number, checked: boolean) {
    if (checked) {
      if (value.some(seat => seat.subscription_id === subscriptionId)) {
        return;
      }

      onChange([...value, { subscription_id: subscriptionId, role: '' }]);
      return;
    }

    onChange(value.filter(seat => seat.subscription_id !== subscriptionId));
  }

  function setSubscriptionRole(subscriptionId: number, role: string) {
    onChange(
      value.map(seat =>
        seat.subscription_id === subscriptionId ? { ...seat, role } : seat
      )
    );
  }

  function getSeatRole(subscriptionId: number) {
    return (
      value.find(seat => seat.subscription_id === subscriptionId)?.role ?? ''
    );
  }

  const hasRoleErrors = Object.keys(roleErrors).length > 0;

  return (
    <fieldset
      id={groupId}
      aria-invalid={hasRoleErrors || Boolean(apiError)}
      className="p-0 border-0 min-w-0 font-sans"
    >
      <legend className="sr-only">
        {i18n.ft(messages.fields.subscriptions)}
      </legend>

      <div className="flex flex-col rounded-lg border border-[#889EBB] overflow-hidden divide-y divide-[#889EBB]">
        {subscriptions.map((subscription, index) => {
          const isChecked = selectedIds.includes(subscription.id);
          const remainingSeats = getRemainingSeats(subscription);
          const hasSeatsAvailable = () => {
            if (subscription.product.name === 'data_collection') return true;
            return remainingSeats === null || remainingSeats > 0;
          };
          const isDisabled = !hasSeatsAvailable() && !isChecked;
          const requiresRole = subscriptionRequiresRole(
            subscription.product.name
          );
          const roleOptions = requiresRole
            ? getSubscriptionProductRoles(subscription.product.name)
            : [];
          const roleError = roleErrors[subscription.id];

          return (
            <div
              key={subscription.id}
              className={clsx(
                'flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4',
                index % 2 === 0 ? 'bg-white' : 'bg-[#F7F8F9]',
                isChecked && !isDisabled
                  ? 'border-action'
                  : 'border-transparent'
              )}
            >
              <div className="min-w-0 flex-1">
                <CheckboxWithLabel
                  id={`${groupId}-${subscription.id}`}
                  label={formatSubscriptionLabel(subscription)}
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={checked =>
                    toggleSubscription(subscription.id, checked)
                  }
                />
              </div>

              {requiresRole ? (
                <div className="w-full sm:w-[14rem] flex-shrink-0 sm:pt-0.5">
                  <Select
                    className={clsx(
                      'gap-0 [&_button]:py-1.5 [&_span]:text-sm [&_label]:text-sm',
                      roleError &&
                        '[&_button]:border-[#AC213A] [&_button]:ring-1 [&_button]:ring-[#AC213A]'
                    )}
                    optionsClassName="text-sm"
                    value={
                      isChecked ? getSeatRole(subscription.id) || null : null
                    }
                    placeholder={i18n.ft(
                      messages.fields.subscriptionRolePlaceholder
                    )}
                    onChange={role =>
                      setSubscriptionRole(subscription.id, String(role ?? ''))
                    }
                    options={roleOptions}
                    disabled={!isChecked || isDisabled}
                    label={i18n.ft(messages.fields.subscriptionRole)}
                    required={isChecked}
                  />
                  {roleError && (
                    <p className="font-sans text-sm text-[#AC213A] mt-1 m-0">
                      {roleError}
                    </p>
                  )}
                </div>
              ) : (
                <div className="w-full sm:w-[14rem] flex-shrink-0 sm:pt-0.5 text-sm text-center">
                  {i18n.ft(messages.fields.noRoleRequired)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {apiError && (
        <p className="font-sans text-sm text-[#AC213A] mt-2 m-0">{apiError}</p>
      )}
    </fieldset>
  );
}
